import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal, untracked, viewChild } from "@angular/core";
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatCellDef,
  MatHeaderRowDef,
  MatRowDef,
  MatHeaderCell,
  MatCell,
  MatHeaderRow,
  MatRow,
} from "@angular/material/table";
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from "@angular/material/card";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import type { Feature, FeatureCollection } from "geojson";
import type { ColumnDef, FeatureActionEvent, LayerDescriptor } from "./feature-table.types";
import { FeatureDetailPanelService } from "../feature-detail-panel/feature-detail-panel.service";

/**
 * Displays a GeoJSON FeatureCollection as a paginated Material table.
 * Data source is abstracted behind LayerDescriptor.loadPage — the component is
 * agnostic about whether data comes from memory, file store, or an endpoint. Renders as its
 * own outlined card; the parent panel owns the surrounding scroll container.
 */
@Component({
  selector: "lib-feature-table",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRowDef,
    MatRowDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardTitle,
    MatPaginator,
    MatProgressBar,
    MatIconButton,
    MatButton,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
  templateUrl: "./feature-table.component.html",
  styleUrl: "./feature-table.component.scss",
})
export class FeatureTableComponent {
  readonly layer = input.required<LayerDescriptor>();
  readonly featureAction = output<FeatureActionEvent>();

  protected readonly features = signal<Feature[]>([]);
  protected readonly loading = signal<boolean>(false);
  protected readonly totalFeatures = signal<number | undefined>(undefined);
  protected readonly objectKeys = Object.keys;

  private currentPage = 0;
  private pageSize = 100;

  protected readonly columnDefs: ColumnDef[] = [
    { id: "index", visible: false, sortable: false, filterable: false },
    { id: "geometry_type", visible: true, sortable: false, filterable: false },
    { id: "properties", visible: true, sortable: false, filterable: false },
    { id: "actions", visible: true, sortable: false, filterable: false },
  ];

  protected readonly displayedColumns: string[] = this.columnDefs
    .filter((c) => c.visible)
    .map((c) => c.id);

  private readonly paginator = viewChild(MatPaginator);

  private readonly featureDetailPanel = inject(FeatureDetailPanelService);

  constructor() {
    effect(() => {
      const descriptor = this.layer();
      untracked(() => this.paginator()?.firstPage());
      this.loadPage(descriptor, 0);
    });
  }

  private loadPage(descriptor: LayerDescriptor, page: number): void {
    const ps = descriptor.pageSize ?? 100;
    this.pageSize = ps;
    this.currentPage = page;

    if (page === 0) {
      this.features.set([]);
      this.totalFeatures.set(descriptor.totalFeatures);
    }

    this.loading.set(true);
    descriptor
      .loadPage(page, ps)
      .then((collection: FeatureCollection) => {
        const loaded = collection.features ?? [];
        this.features.set(loaded);
        if (descriptor.totalFeatures != null) {
          this.totalFeatures.set(descriptor.totalFeatures);
        } else if (loaded.length < ps) {
          this.totalFeatures.set(page * ps + loaded.length);
        }
      })
      .catch((err: unknown) => {
        console.error("FeatureTableComponent: error loading page", page, err);
      })
      .finally(() => {
        this.loading.set(false);
      });
  }

  /**
   * Resolves the display identifier for a feature row.
   * Priority: feature.id → properties.id/ID → 1-based global index.
   */
  protected resolveFeatureId(feature: Feature, rowIndex: number): string | number {
    if (feature.id != null) return feature.id as string | number;
    const props = feature.properties ?? {};
    if (props["id"] != null) return props["id"] as string | number;
    if (props["ID"] != null) return props["ID"] as string | number;
    return this.currentPage * this.pageSize + rowIndex + 1;
  }

  protected openFeatureDetail(feature: Feature): void {
    const descriptor = this.layer();
    const allFeatures = descriptor.getAllFeatures?.() ?? this.features();
    const startIndex = Math.max(
      0,
      allFeatures.findIndex((f) => f === feature || (feature.id != null && f.id === feature.id)),
    );
    this.featureDetailPanel.open({
      layerName: descriptor.layerName,
      features: allFeatures,
      startIndex,
      sourceEpsg: descriptor.sourceEpsg,
      onUpdateProperties: descriptor.updateFeatureProperties,
    });
  }

  protected onZoomToFeature(feature: Feature): void {
    this.featureAction.emit({ action: "zoom_to_feature", feature });
  }

  protected onCopyJson(feature: Feature): void {
    navigator.clipboard.writeText(JSON.stringify(feature, null, 2)).catch((err: unknown) => {
      console.error("FeatureTableComponent: clipboard write failed", err);
    });
  }

  protected onPageChange(event: PageEvent): void {
    this.loadPage(this.layer(), event.pageIndex);
  }
}
